import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Zap, AlertTriangle, Target, Calculator } from "lucide-react";
import { api, http, errMsg } from "@/lib/api";
import CoinIcon from "./CoinIcon";
import "./NewHedgeModal.css";

// Hızlı hedef kâr seçenekleri (yatırılan tutarın yüzdesi)
const TARGET_PRESETS = [
  { label: "%2", roi: 2 },
  { label: "%5", roi: 5 },
  { label: "%10", roi: 10 },
  { label: "%20", roi: 20 },
];

const MIN_LEVERAGE = 3;
const MAX_LEVERAGE = 25;
const SL_TO_TP_RATIO = 2; // risk/reward 1:2

/**
 * Hedge açma parametrelerinden TP/SL/kaldıraç türetir.
 *
 * Formül:
 *  - ROI hedefi = (hedef_kâr / tutar) × 100
 *  - Notional = tutar × kaldıraç
 *  - TP yüzdesi (fiyat hareketi) = hedef_kâr / notional × 100
 *  - SL yüzdesi = TP × 2 (default risk/reward 1:2)
 *
 * Akıllı kaldıraç: ROI yüzdesinin yarısı kadar, [3, 25] arasında clamp'lenir.
 * Düşük ROI hedefiyle yüksek kaldıraç gereksiz, yüksek ROI hedefiyle
 * mantıklı bir taban oluşur.
 */
function computeAutoParams(amount, targetProfit) {
  const a = Number(amount) || 0;
  const t = Number(targetProfit) || 0;
  if (a <= 0 || t <= 0) return null;
  const roiPct = (t / a) * 100;
  // Otomatik kaldıraç: ROI'nin yarısı, clamp [3, 25]
  let leverage = Math.ceil(roiPct / 2);
  leverage = Math.max(MIN_LEVERAGE, Math.min(MAX_LEVERAGE, leverage));
  const notional = a * leverage;
  const tpPct = (t / notional) * 100;
  const slPct = tpPct * SL_TO_TP_RATIO;
  return { roiPct, leverage, notional, tpPct, slPct };
}

export default function NewHedgeModal({ open, onClose, initialSymbol, initialCounter, initialAmount, initialTarget, initialLeverage }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [symbol, setSymbol] = useState(initialSymbol || "ETHUSDT");
  const [amount, setAmount] = useState(initialAmount || 50);
  const [targetProfit, setTargetProfit] = useState(initialTarget || 5);
  const [counter, setCounter] = useState(initialCounter || "BINANCE");
  const [manualLeverage, setManualLeverage] = useState(initialLeverage ?? null); // null = auto

  const { data: meta } = useQuery({
    queryKey: ["eligible-pairs"],
    queryFn: () => http.get("/meta/eligible-pairs").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"], queryFn: api.getSettings,
  });
  const { data: prices } = useQuery({
    queryKey: ["prices", symbol],
    queryFn: () => api.prices(symbol),
    refetchInterval: open ? 5000 : false,
    enabled: open,
  });

  // open=true olduğunda initialProps gelirse state'i güncelle
  useEffect(() => {
    if (!open) return;
    if (initialSymbol) setSymbol(initialSymbol);
    if (initialCounter) setCounter(initialCounter);
    if (initialAmount) setAmount(initialAmount);
    if (initialTarget) setTargetProfit(initialTarget);
    if (initialLeverage != null) setManualLeverage(initialLeverage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!settings) return;
    if (settings.signal_symbol) setSymbol(settings.signal_symbol);
    if (settings.preferred_counter_exchange) setCounter(settings.preferred_counter_exchange);
    if (settings.default_position_size_usd) setAmount(settings.default_position_size_usd);
  }, [settings]);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const auto = useMemo(
    () => computeAutoParams(amount, targetProfit),
    [amount, targetProfit],
  );

  const effectiveLeverage = manualLeverage ?? auto?.leverage ?? 10;
  const effectiveNotional = Number(amount) * effectiveLeverage;
  const effectiveTpPct = effectiveNotional > 0
    ? (Number(targetProfit) / effectiveNotional) * 100
    : 0;
  const effectiveSlPct = effectiveTpPct * SL_TO_TP_RATIO;

  // Slippage tahmini için token cinsinden qty (yaklaşık).
  // Gerçek qty backend'te tam hesaplanır; UI'da preview için darkex mid'e böl.
  const previewPrice = prices?.darkex?.price || prices?.binance?.price;
  const previewQty = previewPrice > 0
    ? effectiveNotional / previewPrice
    : 0;

  // Karşı bacak slippage'ı (BINANCE long → BUY, OKX skip)
  const { data: slipQuote } = useQuery({
    queryKey: ["slip-quote", counter, symbol, previewQty],
    queryFn: () =>
      counter === "BINANCE" && previewQty > 0
        ? api.execQuote(counter, symbol, "BUY", previewQty)
        : null,
    enabled: open && counter === "BINANCE" && previewQty > 0,
    refetchInterval: open ? 5000 : false,
    staleTime: 3000,
    retry: false,
  });

  // İki bacak arası yaş farkı (latency disparity göstergesi)
  const { data: ageGap } = useQuery({
    queryKey: ["age-gap", counter, symbol],
    queryFn: () =>
      counter === "BINANCE"
        ? http.get("/execution/age-gap", {
            params: { ex_a: "BINANCE", ex_b: "DARKEX", symbol },
          }).then((r) => r.data)
        : null,
    enabled: open && counter === "BINANCE",
    refetchInterval: open ? 1000 : false,
    retry: false,
  });

  // 🧩 Smart Entry chunk plan preview — Darkex (zarar) bacağı için.
  // Darkex tarafı: SHORT (SELL). Önizleme bu yönde yapılır.
  const smartEnabled = settings?.smart_entry_enabled !== false;
  const maxSlipBps = settings?.smart_entry_max_slippage_bps ?? 5;
  const maxChunks = settings?.smart_entry_max_chunks ?? 8;
  const { data: chunkPlan } = useQuery({
    queryKey: ["chunk-plan", "DARKEX", symbol, previewQty, maxSlipBps, maxChunks],
    queryFn: () =>
      previewQty > 0
        ? http.get("/execution/plan", {
            params: {
              exchange: "DARKEX",
              symbol,
              side: "SELL",
              qty: previewQty,
              max_slippage_bps: maxSlipBps,
              max_chunks: maxChunks,
            },
          }).then((r) => r.data)
        : null,
    enabled: open && smartEnabled && previewQty > 0,
    refetchInterval: open ? 5000 : false,
    staleTime: 3000,
    retry: false,
  });

  const mut = useMutation({
    mutationFn: async () => {
      // Progressive feedback — chunked exec uzun sürebilir, kullanıcı boşlukta kalmasın
      const toastId = toast.loading(
        `${symbol} hedge planlanıyor (Smart Entry${chunkPlan?.chunks_count > 1 ? `, ${chunkPlan.chunks_count} chunk` : ""})...`,
      );
      try {
        const result = await api.openNow({
          symbol,
          amount_usd: Number(amount),
          leverage: effectiveLeverage,
          counter_exchange: counter,
          tp_pct: Number(effectiveTpPct.toFixed(3)),
          sl_pct: Number(effectiveSlPct.toFixed(3)),
        });
        toast.dismiss(toastId);
        return result;
      } catch (err) {
        toast.dismiss(toastId);
        throw err;
      }
    },
    onSuccess: (data) => {
      const se = data?.position?.smart_entry || data?.smart_entry;
      const summary = se?.darkex || se?.execution
        ? ` · ${se.darkex?.plan?.chunks || 1} chunk · ${se.darkex?.execution?.elapsed_ms || "?"}ms`
        : "";
      toast.success(`${symbol} hedge açıldı${summary}`);
      qc.invalidateQueries({ queryKey: ["positions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard-live"] });
      onClose?.();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!open) return null;

  const pairs = meta?.pairs || ["ETHUSDT", "BTCUSDT", "SOLUSDT", "BNBUSDT"];
  const darkexPx = prices?.darkex?.price;
  const counterPx = prices?.[counter.toLowerCase()]?.price;
  const spreadPct = prices?.best_spread_pct;
  const invalid = !auto || Number(amount) <= 0 || Number(targetProfit) <= 0;

  return (
    <div className="hx-ov show" data-testid="new-hedge-modal" onClick={onClose}>
      <div className="hx-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="hx-close" onClick={onClose}
                data-testid="new-hedge-close" aria-label="Kapat">
          <X size={16} />
        </button>

        <div className="hx-body">
          <div className="hx-ic">
            <Zap size={22} strokeWidth={1.9} />
          </div>
          <div className="eb">Yeni Hedge</div>
          <h3>İşlem Aç</h3>
          <p>
            Yatırılacak tutar + edilmek istenen kâr → sistem otomatik
            <b> kaldıraç</b>, <b>TP</b> ve <b>SL</b> uygular.
          </p>

          <div className="hx-form">
            {/* Symbol */}
            <label className="hx-field">
              <span>Sembol</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CoinIcon symbol={symbol} size="md" />
                <select value={symbol} onChange={(e) => setSymbol(e.target.value)}
                        data-testid="new-hedge-symbol"
                        style={{ flex: 1 }}>
                  {pairs.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </div>
            </label>

            {/* Counter exchange */}
            <label className="hx-field">
              <span>Karşı Borsa</span>
              <select value={counter} onChange={(e) => setCounter(e.target.value)}
                      data-testid="new-hedge-counter">
                <option value="BINANCE">Binance</option>
                <option value="OKX" disabled>OKX (yakında — L2 feed eksik)</option>
              </select>
              {counter === "OKX" && (
                <span style={{
                  fontSize: 10, color: "#fbbf24", marginTop: 4,
                  letterSpacing: "0.04em",
                }}>
                  ⚠️ OKX entegrasyonu yarım: spread/slippage guard'ı yok.
                  Binance kullan.
                </span>
              )}
            </label>

            {/* Amount */}
            <label className="hx-field">
              <span>Yatırılacak Tutar (USDT)</span>
              <input type="number" min="10" step="5" value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     data-testid="new-hedge-amount" />
            </label>

            {/* Target Profit */}
            <label className="hx-field">
              <span>
                <Target size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                Hedef Kâr (USDT)
              </span>
              <input type="number" min="0.5" step="0.5" value={targetProfit}
                     onChange={(e) => setTargetProfit(e.target.value)}
                     data-testid="new-hedge-target-profit" />
            </label>

            {/* Quick ROI presets */}
            <div className="hx-presets" data-testid="new-hedge-target-presets">
              {TARGET_PRESETS.map((p) => {
                const tgt = +((Number(amount) * p.roi) / 100).toFixed(2);
                const active = Math.abs(Number(targetProfit) - tgt) < 0.01;
                return (
                  <button key={p.label}
                          type="button"
                          className={active ? "on" : ""}
                          onClick={() => setTargetProfit(tgt)}>
                    {p.label} (${tgt})
                  </button>
                );
              })}
            </div>

            {/* Manual leverage toggle (advanced) */}
            <label className="hx-field hx-field-row">
              <span>Kaldıraç</span>
              <select value={manualLeverage ?? "auto"}
                      onChange={(e) =>
                        setManualLeverage(
                          e.target.value === "auto" ? null : Number(e.target.value),
                        )
                      }
                      data-testid="new-hedge-leverage">
                <option value="auto">
                  Otomatik {auto ? `(${auto.leverage}×)` : ""}
                </option>
                {[3, 5, 10, 15, 20, 25].map((l) => (
                  <option key={l} value={l}>{l}×</option>
                ))}
              </select>
            </label>
          </div>

          {/* Auto-calculated preview */}
          <div className="hx-preview" data-testid="new-hedge-preview">
            <div className="hx-preview-row">
              <span className="lbl">Kaldıraç</span>
              <b data-testid="preview-leverage">{effectiveLeverage}×</b>
            </div>
            <div className="hx-preview-row">
              <span className="lbl">Notional</span>
              <b>${effectiveNotional.toLocaleString()}</b>
            </div>
            <div className="hx-preview-row">
              <span className="lbl">TP (kâr al)</span>
              <b className="ok" data-testid="preview-tp">
                +%{effectiveTpPct.toFixed(3)}
                {" "}<span className="hint">(= +${Number(targetProfit).toFixed(2)})</span>
              </b>
            </div>
            <div className="hx-preview-row">
              <span className="lbl">SL (zarar dur)</span>
              <b className="bad" data-testid="preview-sl">
                −%{effectiveSlPct.toFixed(3)}
                {" "}<span className="hint">
                  (≈ −${(Number(targetProfit) * SL_TO_TP_RATIO).toFixed(2)})
                </span>
              </b>
            </div>
            <div className="hx-preview-row">
              <span className="lbl">Risk/Reward</span>
              <b>1 : {(1 / SL_TO_TP_RATIO).toFixed(2)}</b>
            </div>
            {ageGap && ageGap.age_a_ms != null && ageGap.age_b_ms != null && (
              <div className="hx-preview-row"
                   style={{ borderTop: "1px solid rgba(255,255,255,0.06)",
                            paddingTop: 6, marginTop: 4 }}>
                <span className="lbl">⏱️ Latency Gap</span>
                <b
                  data-testid="preview-age-gap"
                  style={{
                    fontSize: 11.5,
                    color: ageGap.ok ? "#4ade80" : "#fb7185",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  BN {Math.round(ageGap.age_a_ms)}ms · DK {Math.round(ageGap.age_b_ms)}ms
                  <span style={{ marginLeft: 6, fontWeight: 500,
                                 color: ageGap.ok ? "#6b7280" : "#fb7185" }}>
                    (Δ {Math.round(ageGap.gap_ms || 0)}ms{!ageGap.ok && " ⚠️"})
                  </span>
                </b>
              </div>
            )}
            {chunkPlan && smartEnabled && (
              <div className="hx-preview-row"
                   style={{ borderTop: "1px solid rgba(255,255,255,0.06)",
                            paddingTop: 6, marginTop: 4 }}>
                <span className="lbl">🧩 Smart Entry</span>
                <b
                  data-testid="preview-chunk-plan"
                  style={{
                    fontSize: 11.5,
                    color: chunkPlan.chunks_count > 1 ? "#fbbf24" : "#4ade80",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {chunkPlan.chunks_count === 1
                    ? "Tek atış"
                    : `${chunkPlan.chunks_count} chunk`}
                  <span style={{ marginLeft: 6, fontWeight: 500, color: "#6b7280" }}>
                    · slip≈{chunkPlan.aggregate_slippage_bps}bps
                    {!chunkPlan.fully_fillable && (
                      <span style={{ color: "#fb7185", marginLeft: 4 }}>
                        ⚠️ Likidite
                      </span>
                    )}
                  </span>
                </b>
              </div>
            )}
            {slipQuote && (
              <>
                <div className="hx-preview-row"
                     style={{ borderTop: "1px solid rgba(255,255,255,0.06)",
                              paddingTop: 6, marginTop: 4 }}>
                  <span className="lbl">📊 Likidite (Book L2)</span>
                  <b style={{ fontSize: 11, fontWeight: 500,
                              color: slipQuote.book_stale ? "#fb7185" : "#9aa1ad" }}>
                    {slipQuote.book_stale
                      ? `Stale (${slipQuote.book_age_sec}s)`
                      : `${slipQuote.levels_used} seviye · ${slipQuote.book_age_sec}s`}
                  </b>
                </div>
                <div className="hx-preview-row">
                  <span className="lbl">Projected Slippage</span>
                  <b
                    className={
                      slipQuote.slippage_pct < 0.1 ? "ok"
                      : slipQuote.slippage_pct < 0.3 ? "" : "bad"
                    }
                    data-testid="preview-slippage"
                  >
                    %{slipQuote.slippage_pct.toFixed(3)}
                    {!slipQuote.fully_fillable && (
                      <span style={{ color: "#fb7185", marginLeft: 4 }}>
                        ⚠️ Likidite yetersiz
                      </span>
                    )}
                  </b>
                </div>
              </>
            )}
          </div>

          <div className="hx-info">
            <AlertTriangle size={14} className="mi" />
            <div>
              {darkexPx ? `Darkex $${Number(darkexPx).toFixed(2)}` : "Darkex —"} /
              {" "}{counterPx ? `${counter} $${Number(counterPx).toFixed(2)}` : `${counter} —`}
              {spreadPct != null && (
                <>
                  {" · "}Spread <b>{spreadPct.toFixed(3)}%</b>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="hx-a">
          <button className="mb" onClick={onClose} data-testid="new-hedge-cancel">
            Vazgeç
          </button>
          <button
            type="button"
            className="mb mb-sim"
            data-testid="new-hedge-simulate-link"
            onClick={() => {
              const params = new URLSearchParams({
                symbol,
                counter: counter,
                amount: String(amount),
                leverage: String(effectiveLeverage),
                target: String(targetProfit),
              });
              onClose?.();
              navigate(`/scenario?${params.toString()}`);
            }}
          >
            <Calculator size={14} /> Önce Simüle Et
          </button>
          <button className="mb go" onClick={() => mut.mutate()}
                  disabled={mut.isPending || invalid}
                  data-testid="new-hedge-submit">
            {mut.isPending ? "Açılıyor…" : "İşlem Aç"}
          </button>
        </div>
      </div>
    </div>
  );
}
